import { Resend } from "resend";
import { logger } from "./logger";

const resend = process.env["RESEND_API_KEY"]
  ? new Resend(process.env["RESEND_API_KEY"])
  : null;

const FROM = "AI Tender Copilot <noreply@tender-copilot.sa>";

export interface MailOptions {
  to: string;
  subject: string;
  html: string;
}

export async function sendMail(opts: MailOptions): Promise<void> {
  if (!resend) {
    logger.warn({ to: opts.to, subject: opts.subject }, "RESEND_API_KEY not set – email skipped");
    return;
  }
  try {
    await resend.emails.send({ from: FROM, to: opts.to, subject: opts.subject, html: opts.html });
    logger.info({ to: opts.to, subject: opts.subject }, "Email sent");
  } catch (err) {
    logger.error({ err, to: opts.to }, "Failed to send email");
  }
}

export function welcomeHtml(firstName: string, isAr: boolean): string {
  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">مرحباً بك يا ${firstName} في المساعد الذكي للمناقصات السعودية</h2>
        <p>يسعدنا انضمامك إلينا. يمكنك الآن تحليل المناقصات الحكومية، تقييم الجدوى، وإنشاء مقترحات احترافية.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">Welcome to AI Tender Copilot Saudi, ${firstName}!</h2>
        <p>We're glad you joined us. You can now analyze government tenders, evaluate bid worthiness, and generate professional proposals.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}

export function deadlineReminderHtml(firstName: string, tenderTitle: string, deadline: Date, daysLeft: number, isAr: boolean): string {
  const dStr = deadline.toLocaleDateString(isAr ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" });
  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#dc2626">تذكير بموعد نهائي – ${tenderTitle}</h2>
        <p>عزيزي ${firstName}، تبقّى <strong>${daysLeft} أيام</strong> على الموعد النهائي لتقديم المناقصة.</p>
        <p>الموعد النهائي: <strong>${dStr}</strong></p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#dc2626">Deadline Reminder – ${tenderTitle}</h2>
        <p>Hi ${firstName}, you have <strong>${daysLeft} day(s)</strong> left to submit your bid.</p>
        <p>Deadline: <strong>${dStr}</strong></p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}

export function analysisCompleteHtml(firstName: string, tenderTitle: string, decision: string, isAr: boolean): string {
  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">اكتمل تحليل المناقصة – ${tenderTitle}</h2>
        <p>عزيزي ${firstName}، اكتمل تحليل الذكاء الاصطناعي للمناقصة.</p>
        <p>التوصية: <strong>${decision}</strong></p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">Analysis Complete – ${tenderTitle}</h2>
        <p>Hi ${firstName}, the AI analysis for your tender is complete.</p>
        <p>Recommendation: <strong>${decision}</strong></p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}

export function quoteReceivedHtml(
  vendorFirstName: string,
  buyerName: string,
  subject: string,
  message: string | undefined | null,
  items: Array<{ name: string; qty: number; unit?: string }>,
  isAr: boolean,
): string {
  const itemsHtml = items.length
    ? `<ul style="padding-inline-start:20px;margin:8px 0">${items.map((i) => `<li>${i.name} × ${i.qty}${i.unit ? ` ${i.unit}` : ""}</li>`).join("")}</ul>`
    : "";
  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">طلب سعر جديد – ${subject}</h2>
        <p>عزيزي ${vendorFirstName}، وصلك طلب سعر جديد من <strong>${buyerName}</strong>.</p>
        <p><strong>الموضوع:</strong> ${subject}</p>
        ${message ? `<p><strong>الرسالة:</strong> ${message}</p>` : ""}
        ${itemsHtml ? `<p><strong>المنتجات المطلوبة:</strong></p>${itemsHtml}` : ""}
        <p>يرجى الردّ على طلب السعر في أقرب وقت ممكن من لوحة التحكم.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">New Quote Request – ${subject}</h2>
        <p>Hi ${vendorFirstName}, you have a new quote request from <strong>${buyerName}</strong>.</p>
        <p><strong>Subject:</strong> ${subject}</p>
        ${message ? `<p><strong>Message:</strong> ${message}</p>` : ""}
        ${itemsHtml ? `<p><strong>Requested items:</strong></p>${itemsHtml}` : ""}
        <p>Please log in to your vendor dashboard to respond as soon as possible.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}

export function quoteRespondedHtml(
  buyerFirstName: string,
  vendorCompanyName: string,
  subject: string,
  vendorResponse: string | undefined | null,
  quotedPrice: string | undefined | null,
  status: string,
  isAr: boolean,
): string {
  const statusLabels: Record<string, [string, string]> = {
    responded: ["ردّ بسعر", "responded with a quote"],
    accepted: ["قبِل طلبك", "accepted your request"],
    declined: ["رفض طلبك", "declined your request"],
  };
  const [arStatus, enStatus] = statusLabels[status] ?? ["ردّ على طلبك", "responded to your request"];

  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">ردّ على طلب السعر – ${subject}</h2>
        <p>عزيزي ${buyerFirstName}، المورّد <strong>${vendorCompanyName}</strong> ${arStatus}.</p>
        ${quotedPrice ? `<p><strong>السعر المقترح:</strong> ${quotedPrice}</p>` : ""}
        ${vendorResponse ? `<p><strong>رسالة المورّد:</strong> ${vendorResponse}</p>` : ""}
        <p>يمكنك الاطلاع على التفاصيل الكاملة في لوحة التحكم.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">Quote Reply – ${subject}</h2>
        <p>Hi ${buyerFirstName}, vendor <strong>${vendorCompanyName}</strong> ${enStatus}.</p>
        ${quotedPrice ? `<p><strong>Quoted price:</strong> ${quotedPrice}</p>` : ""}
        ${vendorResponse ? `<p><strong>Vendor message:</strong> ${vendorResponse}</p>` : ""}
        <p>Log in to view the full details and next steps.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}

export function subscriptionEventHtml(firstName: string, event: "created" | "cancelled" | "invoice_paid", planName: string, isAr: boolean): string {
  const msgs: Record<typeof event, [string, string]> = {
    created: ["تم تفعيل اشتراكك", `Subscription activated – ${planName}`],
    cancelled: ["تم إلغاء اشتراكك", `Subscription cancelled – ${planName}`],
    invoice_paid: ["تم الدفع بنجاح", `Invoice paid – ${planName}`],
  };
  const [arMsg, enMsg] = msgs[event];
  return isAr
    ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">${arMsg}</h2>
        <p>عزيزي ${firstName}، ${arMsg} (${planName}).</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`
    : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
        <h2 style="color:#1e40af">${enMsg}</h2>
        <p>Hi ${firstName}, ${enMsg}.</p>
        <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
      </div>`;
}
